import axios from "axios";

const API_URL = import.meta.env.VITE_API_URL || "";

/**
 * Safely extract an error message string from an Axios error.
 * Handles cases where `detail` is an object (e.g. plan-limit errors) and
 * preflight validation responses (`{ message, errors: [{ platform, message }] }`).
 */
export function getErrorMessage(err: unknown, fallback = "Something went wrong"): string {
  const axiosErr = err as { response?: { data?: { detail?: unknown } } } | undefined;
  const detail = axiosErr?.response?.data?.detail;
  if (typeof detail === "string") return detail;
  if (detail && typeof detail === "object") {
    const d = detail as { message?: string; errors?: Array<{ platform?: string; message?: string }> };
    // Preflight-style failures: prefer the first platform-specific message
    // so the user sees "TikTok requires video content" instead of the
    // generic "Pre-flight validation failed".
    if (Array.isArray(d.errors) && d.errors.length > 0) {
      const first = d.errors[0];
      if (first?.message) {
        const plat = first.platform ? `${first.platform.charAt(0).toUpperCase()}${first.platform.slice(1)}: ` : "";
        return d.errors.length > 1
          ? `${plat}${first.message} (+${d.errors.length - 1} more)`
          : `${plat}${first.message}`;
      }
    }
    return d.message || fallback;
  }
  return fallback;
}

/** Progress callback: (loaded bytes, total bytes) */
export type UploadProgressCallback = (loaded: number, total: number) => void;

/** Format byte count into a human-readable string (e.g. "2.5 MB") */
export function formatBytes(bytes: number, decimals = 1): string {
  if (bytes === 0) return "0 B";
  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB"];
  const i = Math.min(Math.floor(Math.log(bytes) / Math.log(k)), sizes.length - 1);
  const value = bytes / Math.pow(k, i);
  return `${value.toFixed(i === 0 ? 0 : decimals)} ${sizes[i]}`;
}

/**
 * Retry a fetch-based upload with exponential backoff.
 * Used for presigned S3 uploads which can transiently fail.
 */
async function fetchWithRetry(
  url: string,
  init: RequestInit,
  maxRetries = 3
): Promise<Response> {
  let lastError: Error | null = null;
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      const resp = await fetch(url, init);
      if (resp.ok || resp.status < 500) return resp; // don't retry 4xx
      lastError = new Error(`Upload returned ${resp.status}`);
    } catch (err: unknown) {
      lastError = err as Error;
    }
    if (attempt < maxRetries) {
      await new Promise((r) => setTimeout(r, 1000 * Math.pow(2, attempt)));
    }
  }
  throw lastError || new Error("Upload failed after retries");
}

/**
 * Upload a blob to a presigned S3 URL using XMLHttpRequest for progress tracking.
 * Falls back to fetch when progress tracking is not needed.
 */
function xhrUploadToS3(
  url: string,
  body: Blob,
  contentType: string,
  onProgress?: UploadProgressCallback
): Promise<void> {
  if (!onProgress) {
    // No progress needed — use simpler fetch path
    return fetchWithRetry(url, {
      method: "PUT",
      body,
      headers: { "Content-Type": contentType },
    }).then(() => undefined);
  }
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("PUT", url, true);
    xhr.setRequestHeader("Content-Type", contentType);
    xhr.upload.addEventListener("progress", (e) => {
      if (e.lengthComputable) {
        onProgress(e.loaded, e.total);
      }
    });
    xhr.addEventListener("load", () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve();
      } else {
        reject(new Error(`S3 upload returned ${xhr.status}`));
      }
    });
    xhr.addEventListener("error", () => reject(new Error("S3 upload network error")));
    xhr.addEventListener("abort", () => reject(new Error("S3 upload aborted")));
    xhr.send(body);
  });
}

/** Size threshold above which we use multipart chunked upload (10 MB) */
const CHUNK_SIZE = 10 * 1024 * 1024;

/**
 * Upload a large file in chunks via multipart upload.
 * Each chunk gets its own presigned URL; the backend combines them at the end.
 * Falls back to single-part upload if the backend doesn't support multipart.
 */
async function chunkedUploadToS3(
  file: File,
  presignData: { upload_url: string; s3_key: string },
  onProgress?: UploadProgressCallback
): Promise<void> {
  const totalSize = file.size;
  const numChunks = Math.ceil(totalSize / CHUNK_SIZE);
  let totalLoaded = 0;

  // Try multipart presign endpoint
  let multipartData: { upload_id: string; part_urls: string[] } | null = null;
  try {
    const resp = await api.post("/media/presign-multipart", null, {
      params: { s3_key: presignData.s3_key, parts: numChunks },
    });
    multipartData = resp.data;
  } catch {
    // Backend doesn't support multipart — fall back to single upload
    await xhrUploadToS3(presignData.upload_url, file, file.type, onProgress);
    return;
  }

  if (!multipartData) {
    // Should never happen — catch block returns above — but satisfies TypeScript
    await xhrUploadToS3(presignData.upload_url, file, file.type, onProgress);
    return;
  }

  // Upload each chunk with per-chunk XHR progress
  const etags: string[] = [];
  for (let i = 0; i < numChunks; i++) {
    const start = i * CHUNK_SIZE;
    const end = Math.min(start + CHUNK_SIZE, totalSize);
    const chunk = file.slice(start, end);
    const chunkSize = end - start;
    const partUrl = multipartData.part_urls[i];

    const etag = await new Promise<string>((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open("PUT", partUrl, true);
      xhr.setRequestHeader("Content-Type", file.type);

      // Granular progress within each chunk
      xhr.upload.addEventListener("progress", (e) => {
        if (e.lengthComputable && onProgress) {
          onProgress(totalLoaded + e.loaded, totalSize);
        }
      });

      xhr.addEventListener("load", () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          const etagHeader = xhr.getResponseHeader("ETag") || `"part-${i}"`;
          resolve(etagHeader);
        } else {
          reject(new Error(`Chunk ${i + 1} upload failed: ${xhr.status}`));
        }
      });
      xhr.addEventListener("error", () => reject(new Error(`Chunk ${i + 1} network error`)));
      xhr.addEventListener("abort", () => reject(new Error(`Chunk ${i + 1} aborted`)));
      xhr.send(chunk);
    });

    etags.push(etag);
    totalLoaded += chunkSize;
  }

  // Complete multipart upload
  await api.post("/media/complete-multipart", {
    s3_key: presignData.s3_key,
    upload_id: multipartData.upload_id,
    etags,
  });
}

const api = axios.create({
  baseURL: `${API_URL}/api`,
  headers: { "Content-Type": "application/json" },
});

// Attach auth token to every request
api.interceptors.request.use((config) => {
  const token = localStorage.getItem("access_token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// ---------- Token refresh with mutex (prevents race conditions) ----------
// When multiple requests hit 401 simultaneously (e.g. notification poll +
// chat), only ONE refresh call is made; the others wait for its result.
let refreshPromise: Promise<string | null> | null = null;

/**
 * Singleton token refresh — ensures only one refresh call is in-flight at a
 * time, regardless of how many 401s land simultaneously (axios interceptor,
 * SSE fetch in useAgentChat, etc.).
 *
 * Returns the new access_token string on success, or null (+ redirect to
 * /login) on failure.
 */
export async function refreshAccessTokenOnce(): Promise<string | null> {
  if (!refreshPromise) {
    refreshPromise = refreshAccessToken().finally(() => {
      refreshPromise = null;
    });
  }
  return refreshPromise;
}

async function refreshAccessToken(): Promise<string | null> {
  const refreshToken = localStorage.getItem("refresh_token");
  if (!refreshToken) return null;
  try {
    const { data } = await axios.post(`${API_URL}/api/auth/refresh`, {
      refresh_token: refreshToken,
    });
    localStorage.setItem("access_token", data.access_token);
    localStorage.setItem("refresh_token", data.refresh_token);
    return data.access_token as string;
  } catch {
    localStorage.removeItem("access_token");
    localStorage.removeItem("refresh_token");
    window.location.href = "/login";
    return null;
  }
}

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    // Only attempt refresh once per request (_retry guard)
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      const newToken = await refreshAccessTokenOnce();
      if (newToken) {
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return api(originalRequest);
      }
    }
    // Surface plan-limit errors as a user-friendly toast
    if (
      error.response?.status === 403 &&
      error.response?.data?.detail?.error === "plan_limit_reached"
    ) {
      const detail = error.response.data.detail;
      const event = new CustomEvent("plan-limit-reached", { detail });
      window.dispatchEvent(event);
    }

    // Surface inactive subscription errors
    if (
      error.response?.status === 403 &&
      error.response?.data?.detail?.error === "subscription_inactive"
    ) {
      const detail = error.response.data.detail;
      const event = new CustomEvent("subscription-inactive", { detail });
      window.dispatchEvent(event);
    }

    return Promise.reject(error);
  }
);

// === Auth ===
export const authAPI = {
  register: (data: { email: string; password: string; name: string }) =>
    api.post("/auth/register", data),
  login: (data: { email: string; password: string }) =>
    api.post("/auth/login", data),
  logout: (refresh_token: string) =>
    api.post("/auth/logout", { refresh_token }),
  me: () => api.get("/auth/me"),
};

// === Posts ===
export interface PublishTaskSummary {
  task_id: string;
  task_type: string;
  status: string;
  progress: number | null;
  post_ids: string[];
  published_count: number;
  failed_count: number;
  error: string | null;
  created_at: string | null;
  started_at: string | null;
  completed_at: string | null;
}

export interface RecentPublishTasksResponse {
  tasks: PublishTaskSummary[];
  active_count: number;
}

export interface AgentThreadJobsResponse {
  thread_id: string;
  jobs: PublishTaskSummary[];
  active_count: number;
}

export interface AgentConsoleOverview {
  product: {
    name: string;
    subtitle: string;
    reference: string;
  };
  runtime: Record<string, string>;
  architecture: {
    nodes: Array<{
      id: string;
      label: string;
      detail: string;
      kind: string;
    }>;
    edges: Array<{
      from: string;
      to: string;
      label: string;
      kind: string;
    }>;
  };
  specialists: Array<{
    id: string;
    label: string;
    description: string;
  }>;
  safety: {
    write_guard: boolean;
    approval_interrupts: boolean;
    preflight_checks: string[];
  };
  conversation: {
    recent: Array<{
      conversation_id: string;
      title: string;
      message_count: number;
      tool_ui_count: number;
      pending_confirmation_count: number;
      updated_at: string | null;
    }>;
    recent_count: number;
  };
  publishing: {
    active_count: number;
    recent_tasks: PublishTaskSummary[];
  };
}

export const postsAPI = {
  list: (params?: { status?: string; favorites?: boolean; page?: number; limit?: number }) =>
    api.get("/posts", { params }),
  counts: () => api.get<Record<string, number>>("/posts/counts"),
  get: (id: string) => api.get(`/posts/${id}`),
  creationHistory: (id: string) => api.get(`/posts/${id}/creation-history`),
  create: (data: unknown) => api.post("/posts", data),
  update: (id: string, data: unknown) => api.put(`/posts/${id}`, data),
  delete: (id: string) => api.delete(`/posts/${id}`),
  publishNow: (id: string) => api.post(`/posts/${id}/publish-now`),
  publishTask: (taskId: string) => api.get(`/posts/publish-task/${taskId}`),
  recentPublishTasks: (params?: { limit?: number }) =>
    api.get<RecentPublishTasksResponse>("/posts/publish-tasks/recent", { params }),
  preflight: (
    id: string,
    overrides?: {
      text?: string;
      media_ids?: string[];
      media_variants?: Array<{
        base_media_id: string;
        variant_media_id: string;
        target_platforms: string[];
        slot_index: number;
      }>;
      target_platforms?: string[];
      content_type?: string;
      platform_overrides?: Record<string, { text: string }>;
    },
  ) =>
    api.post(`/posts/${id}/preflight`, overrides || null),
  duplicate: (id: string) => api.post(`/posts/${id}/duplicate`),
  toDraft: (id: string) => api.post(`/posts/${id}/to-draft`),
  retry: (id: string) => api.post(`/posts/${id}/retry`),
  rollback: (id: string) => api.post(`/posts/${id}/rollback`),
  // P3-3 — Dead-letter queue for terminal publish failures.
  deadLetters: (params?: { resolved?: boolean }) =>
    api.get('/posts/dead-letters', { params }),
  resolveDeadLetter: (id: string) =>
    api.post(`/posts/dead-letters/${id}/resolve`),
  toggleFavorite: (id: string) => api.post(`/posts/${id}/favorite`),
  bulkCreate: (posts: unknown[]) => api.post("/posts/bulk/create", { posts }),
  bulkDelete: (ids: string[]) => api.post("/posts/bulk/delete", { post_ids: ids }),
  bulkPublish: (ids: string[]) => api.post("/posts/bulk/publish", { post_ids: ids }),
  calendar: (startDate: string, endDate: string) =>
    api.get("/posts/calendar", { params: { start_date: startDate, end_date: endDate } }),
  // Internal comments
  listComments: (postId: string) => api.get(`/posts/${postId}/comments`),
  addComment: (postId: string, data: { content: string }) =>
    api.post(`/posts/${postId}/comments`, data),
  deleteComment: (postId: string, commentId: string) =>
    api.delete(`/posts/${postId}/comments/${commentId}`),
};

export const agentJobsAPI = {
  listThreadJobs: (threadId: string, params?: { limit?: number }) =>
    api.get<AgentThreadJobsResponse>(
      `/agent/threads/${encodeURIComponent(threadId)}/jobs`,
      { params },
    ),
  getJob: (jobId: string) =>
    api.get<PublishTaskSummary>(`/agent/jobs/${encodeURIComponent(jobId)}`),
};

// === AG-UI Console ===
export const aguiConsoleAPI = {
  overview: () => api.get<AgentConsoleOverview>("/agui/console/overview"),
};

// === Queue ===
export const queueAPI = {
  getSettings: () => api.get("/queue/settings"),
  updateSettings: (data: {
    active_days?: string[];
    time_slots?: Array<{ id: string; time: string; enabled: boolean }>;
    timezone?: string;
    paused?: boolean;
    auto_schedule?: boolean;
  }) => api.put("/queue/settings", data),
  getOptimalTimes: (params?: { platform?: string }) =>
    api.get("/queue/optimal-times", { params }),
  pause: () => api.post("/queue/pause"),
  resume: () => api.post("/queue/resume"),
  listPosts: () => api.get("/queue/posts"),
  reorder: (postId: string, newScheduledAt: string) =>
    api.post("/queue/reorder", { post_id: postId, new_scheduled_at: newScheduledAt }),
  shuffle: () => api.post("/queue/shuffle"),
  addToQueue: (postId: string) => api.post(`/queue/add/${postId}`),
  publishNow: (postId: string) => postsAPI.publishNow(postId),
  removePost: (postId: string) => postsAPI.delete(postId),
};

// === Social Accounts ===
export const socialAPI = {
  platforms: () => api.get("/social-accounts/platforms"),
  list: () => api.get("/social-accounts"),
  connect: (platform: string) => api.get(`/social-accounts/oauth/${platform}`),
  getInstagramSelection: (token: string) =>
    api.get(`/social-accounts/oauth/instagram/selection/${token}`),
  completeInstagramSelection: (token: string, instagramAccountId: string) =>
    api.post(`/social-accounts/oauth/instagram/selection/${token}`, {
      instagram_account_id: instagramAccountId,
    }),
  connectBluesky: (handle: string, appPassword: string) =>
    api.post("/social-accounts/connect/bluesky", { handle, app_password: appPassword }),
  connectTelegram: (botToken: string) =>
    api.post("/social-accounts/connect/telegram", { bot_token: botToken }),
  disconnect: (id: string) => api.delete(`/social-accounts/${id}`),
  sync: (id: string) => api.post(`/social-accounts/${id}/sync`),
};

// === Media ===
export const mediaAPI = {
  upload: (file: File) => {
    const formData = new FormData();
    formData.append("file", file);
    return api.post("/media/upload", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  },
  uploadMultiple: (files: File[]) => {
    const formData = new FormData();
    files.forEach((f) => formData.append("files", f));
    return api.post("/media/upload-multiple", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  },
  directUpload: async (file: File, onProgress?: UploadProgressCallback) => {
    // Step 1: Get presigned URL from backend
    const presignResp = await api.post("/media/presign", null, {
      params: {
        filename: file.name,
        content_type: file.type,
        file_size: file.size,
      },
    });

    const presignData = presignResp.data;

    // Fall back to old upload flow if S3 not configured (local dev)
    if (!presignData.use_direct_upload) {
      const formData = new FormData();
      formData.append("file", file);
      return api.post("/media/upload", formData, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (e) => {
          if (onProgress && e.total) {
            onProgress(e.loaded, e.total);
          }
        },
      });
    }

    // Step 2: Upload directly to S3 — chunked for large files, single for small
    try {
      if (file.size > CHUNK_SIZE) {
        await chunkedUploadToS3(file, presignData, onProgress);
      } else {
        await xhrUploadToS3(presignData.upload_url, file, file.type, onProgress);
      }
    } catch (err: unknown) {
      // CORS errors show as TypeError with no response — fall back to server-side upload
      console.warn("Direct S3 upload failed (likely CORS), falling back to server upload:", (err as Error)?.message);
      const formData = new FormData();
      formData.append("file", file);
      return api.post("/media/upload", formData, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (e) => {
          if (onProgress && e.total) {
            onProgress(e.loaded, e.total);
          }
        },
      });
    }

    // Step 3: Confirm upload with backend — mark as "upload completed"
    if (onProgress) {
      onProgress(file.size, file.size); // Ensure 100% before confirm
    }
    return api.post("/media/confirm-upload", null, {
      params: {
        s3_key: presignData.s3_key,
        filename: file.name,
        content_type: file.type,
        file_size: file.size,
      },
    });
  },
  /**
   * Upload media from a remote URL. The backend validates the URL via HEAD,
   * returns a presigned S3 upload URL, then the browser fetches the remote
   * file and streams it directly to S3 — backend never touches the bytes.
   */
  uploadFromUrl: async (
    url: string,
    onProgress?: (loaded: number, total: number) => void
  ) => {
    // Step 1: Backend validates URL and returns presigned upload URL
    const presignResp = await api.post("/media/presign-from-url", null, {
      params: { url },
    });
    const presign = presignResp.data;

    // Fall back: if S3 not configured, let backend handle it
    if (!presign.use_direct_upload) {
      return api.post("/media/upload", null, {
        params: { url },
      });
    }

    // Step 2: Fetch the remote file in the browser
    let remoteResp: Response;
    try {
      remoteResp = await fetch(url);
      if (!remoteResp.ok) {
        throw new Error(`Failed to fetch media from URL: ${remoteResp.status}`);
      }
    } catch {
      // CORS blocked — use backend proxy
      remoteResp = await fetch(
        `${import.meta.env.VITE_API_URL || ""}/api/media/proxy?url=${encodeURIComponent(url)}`
      );
      if (!remoteResp.ok) {
        throw new Error(`Failed to proxy media from URL: ${remoteResp.status}`);
      }
    }

    const totalSize = presign.content_length || 0;

    // Step 3: Stream to S3 via presigned URL with progress tracking
    const uploadToS3 = async (blob: Blob) => {
      try {
        await fetchWithRetry(presign.upload_url, {
          method: "PUT",
          body: blob,
          headers: { "Content-Type": presign.content_type },
        });
      } catch (err: unknown) {
        console.warn("Direct S3 upload failed (likely CORS), falling back to server upload:", (err as Error)?.message);
        // Fall back to server-side upload with the blob
        const formData = new FormData();
        formData.append("file", new File([blob], presign.filename || "upload", { type: presign.content_type }));
        return api.post("/media/upload", formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      }
      return null; // null means S3 upload succeeded, need confirm step
    };

    let fallbackResult = null;
    if (onProgress && remoteResp.body && totalSize > 0) {
      const reader = remoteResp.body.getReader();
      const chunks: BlobPart[] = [];
      let loaded = 0;
      let isDone = false;
      while (!isDone) {
        const { done, value } = await reader.read();
        if (done) {
          isDone = true;
          break;
        }
        chunks.push(value as unknown as BlobPart);
        loaded += value.length;
        onProgress(loaded, totalSize);
      }
      const blob = new Blob(chunks, { type: presign.content_type });
      fallbackResult = await uploadToS3(blob);
    } else {
      const blob = await remoteResp.blob();
      fallbackResult = await uploadToS3(blob);
    }

    // If fallback was used, return its result directly
    if (fallbackResult) return fallbackResult;

    // Step 4: Confirm with backend to create media record
    return api.post("/media/confirm-upload", null, {
      params: {
        s3_key: presign.s3_key,
        filename: presign.filename,
        content_type: presign.content_type,
        file_size: presign.content_length || 0,
      },
    });
  },
  /**
   * Fetch Open Graph metadata for a URL (link preview cards).
   */
  ogPreview: (url: string) =>
    api.post("/media/og-preview", null, { params: { url } }),
  list: (params?: {
    media_type?: string;
    search?: string;
    tag?: string;
    collection_id?: string;
    favorite?: boolean;
    needs_alt?: boolean;
    source_type?: string;
    include_archived?: boolean;
    page?: number;
    limit?: number;
  }) =>
    api.get("/media", { params }),
  stats: () => api.get("/media/stats"),
  collections: () => api.get("/media/collections"),
  createCollection: (data: { name: string; description?: string; color?: string }) =>
    api.post("/media/collections", data),
  updateCollection: (id: string, data: { name: string; description?: string; color?: string }) =>
    api.put(`/media/collections/${id}`, data),
  deleteCollection: (id: string) => api.delete(`/media/collections/${id}`),
  get: (id: string) => api.get(`/media/${id}`),
  usage: (id: string) => api.get(`/media/${id}/usage`),
  update: (id: string, data: {
    alt_text?: string;
    tags?: string[];
    filename?: string;
    collection_ids?: string[];
    favorite?: boolean;
    internal_notes?: string;
  }) =>
    api.put(`/media/${id}`, data),
  bulkUpdate: (data: {
    media_ids: string[];
    tags?: string[];
    add_tags?: string[];
    remove_tags?: string[];
    collection_ids?: string[];
    favorite?: boolean;
    archived?: boolean;
  }) => api.post("/media/bulk-update", data),
  versions: (id: string) => api.get(`/media/${id}/versions`),
  trim: (id: string, startTime: number, endTime: number) =>
    api.post(`/media/${id}/trim`, {
      start_time: startTime,
      end_time: endTime,
    }),
  delete: (id: string) => api.delete(`/media/${id}`),
  /** Check a media item against a platform's constraints. */
  complianceCheck: (mediaId: string, platform: string, contentType = "post") =>
    api.post("/media/compliance-check", { media_id: mediaId, platform, content_type: contentType }),
  /** Auto-fix a media item to comply with platform requirements.
   *
   * Strategy:
   *   - "pad"  — letterbox/pillarbox to fit the aspect range
   *   - "crop" — center-crop to fit the aspect range
   *   - "trim" — cut the video to the platform's max_video_seconds
   */
  autoFix: (
    mediaId: string,
    platform: string,
    contentType = "post",
    strategy: "pad" | "crop" | "trim" = "pad",
  ) =>
    api.post("/media/auto-fix", {
      media_id: mediaId,
      platform,
      content_type: contentType,
      strategy,
    }),
  /** Poll the status of a background media processing task. */
  taskStatus: (taskId: string) =>
    api.get(`/media/task-status/${taskId}`),
  /** Re-encode a video to reduce file size below platform limits. */
  reEncode: (mediaId: string, platform: string, contentType = "post", targetBitrate?: string) =>
    api.post("/media/re-encode", {
      media_id: mediaId,
      platform,
      content_type: contentType,
      ...(targetBitrate ? { target_bitrate: targetBitrate } : {}),
    }),
  /** Apply video edits (trim, filters, speed, text, crop, rotation, audio). */
  videoEdit: (mediaId: string, edits: Record<string, unknown>) =>
    api.post(`/media/${mediaId}/video-edit`, edits),
  enrich: (mediaId: string, data?: { overwrite_alt_text?: boolean }) =>
    api.post(`/media/${mediaId}/enrich`, data ?? { overwrite_alt_text: false }),
  captionSuggestions: (mediaId: string) =>
    api.post(`/media/${mediaId}/caption-suggestions`),
  bulkAltText: (data: { media_ids: string[]; overwrite_alt_text?: boolean }) =>
    api.post("/media/alt-text/bulk", data),
  makePlatformReady: (
    mediaId: string,
    data: {
      target_platforms: string[];
      content_type?: string;
      strategy?: "pad" | "crop" | "trim";
    }
  ) =>
    api.post(`/media/${mediaId}/make-platform-ready`, data),
  importUrl: (data: { url: string; filename?: string }) =>
    api.post("/media/import/url", data),
  discoverRssImages: (data: { url: string }) =>
    api.post("/media/import/rss/discover", data),
  importRssImages: (data: {
    feed_url: string;
    items: Array<{
      image_url: string;
      title?: string | null;
      item_url?: string | null;
      pub_date?: string | null;
      author?: string | null;
    }>;
  }) => api.post("/media/import/rss", data),
  importListeningMedia: (data: { mention_ids: string[] }) =>
    api.post("/media/import/listening", data),
  createSubmissionLink: (data?: { expires_days?: number }) =>
    api.post("/media/submission-link", data ?? { expires_days: 14 }),
  listSubmissions: () => api.get("/media/submissions"),
  approveSubmission: (submissionId: string, data?: { review_notes?: string }) =>
    api.post(`/media/submissions/${submissionId}/approve`, data ?? {}),
  rejectSubmission: (submissionId: string, data?: { review_notes?: string }) =>
    api.post(`/media/submissions/${submissionId}/reject`, data ?? {}),
  submitPublicAsset: (formData: FormData) =>
    api.post("/media/public-submissions", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    }),
};

export interface MediaSubmissionRecord {
  id: string;
  status: "pending" | "approved" | "rejected" | "archived";
  submitter_name: string | null;
  submitter_email: string | null;
  submitter_note: string | null;
  filename: string;
  mime_type: string;
  media_type: string;
  file_size: number;
  original_url: string;
  thumbnail_url: string | null;
  width: number | null;
  height: number | null;
  duration: number | null;
  source_metadata?: Record<string, unknown> | null;
  approved_media_id: string | null;
  review_notes: string | null;
  reviewed_by: string | null;
  reviewed_at: string | null;
  created_at: string;
  updated_at: string;
}

// === Cloud Storage ===
export const cloudStorageAPI = {
  /** List available providers (Google Drive, Dropbox) with config status. */
  providers: () => api.get("/cloud-storage/providers"),
  /** List user's connected cloud storage accounts. */
  listAccounts: () => api.get("/cloud-storage"),
  /** Start OAuth flow — returns { auth_url } to redirect the user. */
  startOAuth: (provider: string) => api.get(`/cloud-storage/oauth/${provider}`),
  /** Browse media files in a connected account. */
  browse: (accountId: string, params?: { folder_id?: string; query?: string; page_token?: string }) =>
    api.get(`/cloud-storage/${accountId}/browse`, { params }),
  /** Get a temporary download URL for a file (for browser-side streaming to S3). */
  getDownloadUrl: (accountId: string, fileId: string) =>
    api.get(`/cloud-storage/${accountId}/download-url/${fileId}`),
  /** Disconnect a cloud storage account. */
  disconnect: (accountId: string) => api.delete(`/cloud-storage/${accountId}`),
  /**
   * Import a file from cloud storage into S3 via presigned URL flow.
   * 1. Get download URL from cloud provider
   * 2. Browser fetches file from cloud provider
   * 3. Browser streams to S3 via presigned URL
   * 4. Confirm upload with backend
   */
  importFile: async (
    accountId: string,
    fileId: string,
    onProgress?: (loaded: number, total: number) => void
  ) => {
    // Step 1: Get download URL + auth header from backend
    const dlResp = await api.get(`/cloud-storage/${accountId}/download-url/${fileId}`);
    const dl = dlResp.data;

    // Step 2: Get presigned S3 upload URL
    const presignResp = await api.post("/media/presign", null, {
      params: {
        filename: dl.filename,
        content_type: dl.content_type,
        file_size: dl.file_size,
      },
    });
    const presign = presignResp.data;

    if (!presign.use_direct_upload) {
      // Local dev fallback — let backend handle it
      return api.post("/media/upload", null, { params: { url: dl.download_url } });
    }

    // Step 3: Fetch from cloud provider
    const fetchHeaders: Record<string, string> = {};
    if (dl.auth_header) {
      fetchHeaders["Authorization"] = dl.auth_header;
    }
    const remoteResp = await fetch(dl.download_url, { headers: fetchHeaders });
    if (!remoteResp.ok) {
      throw new Error(`Failed to fetch from cloud storage: ${remoteResp.status}`);
    }

    const totalSize = dl.file_size || 0;

    // Step 4: Stream to S3 with progress
    if (onProgress && remoteResp.body && totalSize > 0) {
      const reader = remoteResp.body.getReader();
      const chunks: BlobPart[] = [];
      let loaded = 0;
      let isDone = false;
      while (!isDone) {
        const { done, value } = await reader.read();
        if (done) {
          isDone = true;
          break;
        }
        chunks.push(value as unknown as BlobPart);
        loaded += value.length;
        onProgress(loaded, totalSize);
      }
      const blob = new Blob(chunks, { type: dl.content_type });
      await fetchWithRetry(presign.upload_url, {
        method: "PUT",
        body: blob,
        headers: { "Content-Type": dl.content_type },
      });
    } else {
      const blob = await remoteResp.blob();
      await fetchWithRetry(presign.upload_url, {
        method: "PUT",
        body: blob,
        headers: { "Content-Type": dl.content_type },
      });
    }

    // Step 5: Confirm upload
    return api.post("/media/confirm-upload", null, {
      params: {
        s3_key: presign.s3_key,
        filename: dl.filename,
        content_type: dl.content_type,
        file_size: dl.file_size || 0,
      },
    });
  },
};

// === Pages (Builder) ===
export const pagesAPI = {
  list: () => api.get("/pages"),
  get: (id: string) => api.get(`/pages/${id}`),
  creationHistory: (id: string) => api.get(`/pages/${id}/creation-history`),
  create: (data: {
    title?: string;
    name?: string;
    template_id?: string;
    blocks?: unknown[];
    theme?: Record<string, unknown>;
  } & Record<string, unknown>) =>
    api.post("/pages", data),
  update: (id: string, data: unknown) => api.put(`/pages/${id}`, data),
  delete: (id: string) => api.delete(`/pages/${id}`),
  addBlock: (pageId: string, data: unknown) =>
    api.post(`/pages/${pageId}/blocks`, data),
  updateBlock: (pageId: string, blockIndex: number, data: unknown) =>
    api.put(`/pages/${pageId}/blocks/${blockIndex}`, data),
  deleteBlock: (pageId: string, blockIndex: number) =>
    api.delete(`/pages/${pageId}/blocks/${blockIndex}`),
  publish: (id: string) => api.post(`/pages/${id}/publish`),
  unpublish: (id: string) => api.post(`/pages/${id}/unpublish`),
  // Read-only publish-readiness check. Returns
  // ``{ ok, blockers: [], warnings: [] }``. Consumed by the Studio
  // ``PublishPanel`` / ``PreflightDialog`` before enabling the
  // Publish button.
  preflight: (id: string) => api.post(`/pages/${id}/preflight`),
  // Queue an auto-publish. ``scheduled_at`` is ISO-8601 with
  // timezone; pass ``null`` to clear an existing schedule.
  schedulePublish: (id: string, scheduled_at: string | null) =>
    api.post(`/pages/${id}/schedule`, { scheduled_at }),
  // Rolling audit log of publish / unpublish / schedule events.
  // Newest first. ``limit`` capped at 50 by the backend.
  publishHistory: (id: string, limit: number = 20) =>
    api.get(`/pages/${id}/publish-history`, { params: { limit } }),
  duplicate: (id: string) => api.post(`/pages/${id}/duplicate`),
  analytics: (id: string) => api.get(`/pages/${id}/analytics`),
  groups: () => api.get("/pages/groups/list"),
  templates: () => api.get("/pages/templates/list"),
  // Stateless live preview of a tone-specific theme + sample avatar block.
  // Used by the AI wizard "preview theme" step and by the builder's
  // StylingPanel "try a tone" picker — neither mutates the underlying
  // page until the user commits.
  themePreview: (data: {
    brand_name?: string;
    brand_tagline?: string;
    brand_tone?: string;
    purpose?: string;
    current_theme?: Record<string, unknown>;
    avatar_image_url?: string;
    avatar_fallback_image_url?: string;
    avatar_offline_image_url?: string;
  }) => api.post("/pages/theme-preview", data),
  uploadThumbnail: (pageId: string, base64Image: string) =>
    api.post(`/pages/${pageId}/thumbnail`, { image: base64Image }),
  translatePage: (pageId: string, targetLocale: string) =>
    api.post(`/pages/${pageId}/translate`, { target_locale: targetLocale }),
  // Interactive edit proposal (pending → accept/reject)
  acceptPendingEdit: (pageId: string) =>
    api.post(`/pages/${pageId}/pending/accept`),
  rejectPendingEdit: (pageId: string) =>
    api.post(`/pages/${pageId}/pending/reject`),
  getPublic: (
    slug: string,
    params?: {
      pwd?: string;
      lang?: string;
      preview?: 'draft' | 'pending' | string;
      token?: string;
    },
  ) => api.get(`/p/${slug}`, { params }),
  trackEvent: (
    slug: string,
    data: {
      event_type: 'page_view' | 'block_click' | 'form_submit' | 'payment_start' | 'tip_start';
      block_id?: string;
      block_type?: string;
      link_url?: string;
      link_label?: string;
      visitor_id?: string;
      session_id?: string;
      utm?: Record<string, string>;
      metadata?: Record<string, unknown>;
    },
  ) => api.post(`/p/${slug}/events`, data),
  // New (validated, rate-limited, Turnstile-gated) submit shape —
  // { block_id, answers, turnstile_token?, honeypot? }.
  submitForm: (
    slug: string,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    data: { block_id: string; answers: Record<string, any>; turnstile_token?: string; honeypot?: string },
  ) => api.post(`/p/${slug}/form`, data),
  // File-upload flow for form blocks with `file` field types.
  requestFormUploadToken: (slug: string, body: { block_id: string; field_name: string }) =>
    api.post(`/p/${slug}/form/upload-token`, body),
  createCheckout: (slug: string, priceId: string) => api.post(`/p/${slug}/checkout`, null, { params: { price_id: priceId } }),
  // P3-2 Tip jar — Stripe Checkout created at click time (amount chosen by
  // visitor). The backend validates the amount against the block's preset
  // list / min / max and forwards to Stripe Connect via ``Stripe-Account``.
  createTipJarCheckout: (
    slug: string,
    body: { amount: number; currency?: string; block_id?: string; message?: string },
  ) => api.post(`/p/${slug}/tip-jar/checkout`, body),
};

// === Form submissions (owner inbox) ===
export const submissionsAPI = {
  list: (
    pageId: string,
    params?: {
      status?: string;
      q?: string;
      block_id?: string;
      from?: string;
      to?: string;
      limit?: number;
      skip?: number;
    },
  ) => api.get(`/pages/${pageId}/submissions`, { params }),
  stats: (pageId: string) => api.get(`/pages/${pageId}/submissions/stats`),
  get: (pageId: string, submissionId: string) =>
    api.get(`/pages/${pageId}/submissions/${submissionId}`),
  setStatus: (
    pageId: string,
    submissionId: string,
    body: { status: 'new' | 'read' | 'replied' | 'spam' | 'archived'; owner_notes?: string },
  ) => api.post(`/pages/${pageId}/submissions/${submissionId}/status`, body),
  bulk: (
    pageId: string,
    body: { action: 'mark_read' | 'mark_spam' | 'archive' | 'delete'; ids: string[] },
  ) => api.post(`/pages/${pageId}/submissions/bulk`, body),
  delete: (pageId: string, submissionId: string, opts?: { hard?: boolean }) =>
    api.delete(`/pages/${pageId}/submissions/${submissionId}`, {
      params: opts?.hard ? { hard: true } : undefined,
    }),
  reply: (
    pageId: string,
    submissionId: string,
    body: { subject: string; body: string },
  ) => api.post(`/pages/${pageId}/submissions/${submissionId}/reply`, body),
  exportCsvUrl: (pageId: string, params?: { status?: string; block_id?: string }) => {
    const qs = new URLSearchParams();
    if (params?.status) { qs.set('status', params.status); }
    if (params?.block_id) { qs.set('block_id', params.block_id); }
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return `/api/pages/${pageId}/submissions/export.csv${suffix}`;
  },
};

// === Short Links ===
export const linksAPI = {
  list: (params?: { page?: number; tag?: string; group_id?: string }) =>
    api.get("/links", { params }),
  create: (data: unknown) => api.post("/links", data),
  update: (id: string, data: unknown) => api.put(`/links/${id}`, data),
  analytics: (id: string) => api.get(`/links/${id}/analytics`),
  delete: (id: string) => api.delete(`/links/${id}`),

  // Bulk operations
  bulkDelete: (linkIds: string[]) => api.post("/links/bulk-delete", { link_ids: linkIds }),
  bulkTag: (linkIds: string[], tags: string[], action: "add" | "remove") =>
    api.post("/links/bulk-tag", { link_ids: linkIds, tags, action }),

  // Link groups
  listGroups: () => api.get("/links/groups"),
  createGroup: (data: { name: string; description?: string; color?: string }) =>
    api.post("/links/groups", data),
  updateGroup: (id: string, data: unknown) => api.put(`/links/groups/${id}`, data),
  deleteGroup: (id: string) => api.delete(`/links/groups/${id}`),

  // AI features
  aiUtmBuilder: (data: { url: string; campaign_description: string; platform?: string }) =>
    api.post("/links/ai/utm-builder", data),
  aiGenerateMeta: (data: { url: string }) =>
    api.post("/links/ai/generate-meta", data),
  aiSummary: (linkId: string) => api.get(`/links/ai/summary/${linkId}`),
  aiBulkDescribe: (linkIds: string[]) =>
    api.post("/links/ai/bulk-describe", { link_ids: linkIds }),
};

// === Post Templates ===
export const templatesAPI = {
  list: (params?: Record<string, unknown>) =>
    api.get("/templates", { params }),
  get: (id: string) => api.get(`/templates/${id}`),
  create: (data: unknown) => api.post("/templates", data),
  update: (id: string, data: unknown) => api.put(`/templates/${id}`, data),
  delete: (id: string) => api.delete(`/templates/${id}`),
  duplicate: (id: string) => api.post(`/templates/${id}/duplicate`),
  apply: (id: string, data: unknown) => api.post(`/templates/${id}/apply`, data),
  toggleFavorite: (id: string) => api.post(`/templates/${id}/favorite`),
  createFromPost: (postId: string, params: Record<string, string>) =>
    api.post(`/templates/from-post/${postId}`, null, { params }),
  getTopByCategory: (category: string, data: unknown) =>
    api.post(`/templates/top-by-category/${category}`, data),
  aiSuggest: (data: unknown) => api.post("/templates/ai/suggest", data),
  aiCategorize: (data: { content: string }) => api.post("/templates/ai/categorize", data),
  aiBrandVoice: (data: unknown) => api.post("/templates/ai/brand-voice", data),
  getStats: () => api.get("/templates/stats"),
  updatePerformance: () => api.post("/templates/update-performance"),
  compose: (data: unknown) => api.post("/templates/compose", data),
  aiRemix: (templateId: string, data: unknown) => api.post(`/templates/ai/remix/${templateId}`, data),
  aiRecommend: (data: unknown) => api.post("/templates/ai/recommend", data),
  createVariant: (id: string, data?: unknown) => api.post(`/templates/${id}/create-variant`, data || {}),
  seedDefaults: (force = false) =>
    api.post(`/templates/seed-defaults${force ? "?force=true" : ""}`),
};

export const templateFoldersAPI = {
  list: () => api.get("/templates/folders"),
  create: (data: unknown) => api.post("/templates/folders", data),
  update: (id: string, data: unknown) => api.put(`/templates/folders/${id}`, data),
  delete: (id: string) => api.delete(`/templates/folders/${id}`),
  reorder: (folderIds: string[]) => api.put("/templates/folders/reorder", { folder_ids: folderIds }),
};

// === Users / Profile ===
export const usersAPI = {
  getProfile: () => api.get("/users/profile"),
  updateProfile: (data: { name?: string; avatar_url?: string }) =>
    api.put("/users/profile", data),
  changePassword: (data: { current_password: string; new_password: string }) =>
    api.put("/users/change-password", data),
  deleteAccount: () => api.delete("/users/account"),
  completeOnboarding: (data: { plan?: string; connected_accounts?: string[] }) =>
    api.post("/users/complete-onboarding", data),
};

// === Notifications ===
export const notificationsAPI = {
  list: (params?: { unread_only?: boolean; limit?: number }) =>
    api.get("/notifications", { params }),
  getPreferences: () => api.get("/notifications/preferences"),
  updatePreferences: (data: {
    email_enabled?: boolean;
    in_app_enabled?: boolean;
    email_categories?: Record<string, boolean>;
  }) => api.put("/notifications/preferences", data),
  markRead: (id: string) => api.put(`/notifications/${id}/read`),
  markAllRead: () => api.put("/notifications/read-all"),
  delete: (id: string) => api.delete(`/notifications/${id}`),
};

// === Teams ===
export const teamsAPI = {
  get: () => api.get("/teams"),
  invite: (data: { email: string; name: string; role?: string }) =>
    api.post("/teams/invite", data),
  updateMemberRole: (email: string, data: { role?: string; permissions?: Record<string, boolean> }) =>
    api.put(`/teams/members/${email}/role`, data),
  removeMember: (email: string) => api.delete(`/teams/members/${email}`),
  // Workspace operations
  listWorkspaces: () => api.get("/teams/workspaces"),
  createWorkspace: (data: { name: string; description?: string; color?: string }) =>
    api.post("/teams/workspaces", data),
  switchWorkspace: (workspaceId: string) =>
    api.post(`/teams/workspaces/${workspaceId}/switch`),
  updateWorkspace: (workspaceId: string, data: { name?: string; description?: string; color?: string }) =>
    api.put(`/teams/workspaces/${workspaceId}`, data),
  deleteWorkspace: (workspaceId: string) =>
    api.delete(`/teams/workspaces/${workspaceId}`),
  getWhiteLabel: (teamId: string) =>
    api.get(`/teams/${teamId}/white-label`),
  updateWhiteLabel: (teamId: string, data: Record<string, unknown>) =>
    api.put(`/teams/${teamId}/white-label`, data),
};

// === Activity Logs ===
export const activityAPI = {
  list: (params?: { action_type?: string; limit?: number; offset?: number; start_date?: string; end_date?: string }) =>
    api.get("/activity", { params }),
};

// === Analytics ===
export const analyticsAPI = {
  overview: (period?: string) => api.get("/analytics/overview", { params: { period } }),
  posts: (params?: { period?: string; platform?: string; sort_by?: string }) =>
    api.get("/analytics/posts", { params }),
  engagementTimeline: (params?: { period?: string; platform?: string }) =>
    api.get("/analytics/engagement-timeline", { params }),
  platformDistribution: (period?: string) =>
    api.get("/analytics/platform-distribution", { params: { period } }),
  contentPerformance: (period?: string) =>
    api.get("/analytics/content-performance", { params: { period } }),
  bestTimes: () => api.get("/analytics/best-times"),
  profile: (period?: string) => api.get(`/analytics/profile`, { params: { period: period || '30d' } }),
  audience: () => api.get(`/analytics/audience`),
  stories: (period?: string) => api.get(`/analytics/stories`, { params: { period: period || '30d' } }),
  sync: () => api.post("/analytics/sync"),
};

// === RSS Feeds ===
export const rssAPI = {
  list: () => api.get("/rss"),
  create: (data: unknown) => api.post("/rss", data),
  get: (id: string) => api.get(`/rss/${id}`),
  update: (id: string, data: unknown) => api.put(`/rss/${id}`, data),
  delete: (id: string) => api.delete(`/rss/${id}`),
  toggle: (id: string) => api.post(`/rss/${id}/toggle`),
  refresh: (id: string) => api.post(`/rss/${id}/refresh`),
  getItems: (id: string) => api.get(`/rss/${id}/items`),
  postItem: (feedId: string, params: { item_guid: string; item_title: string; item_url?: string; item_image?: string }) =>
    api.post(`/rss/${feedId}/post-item`, null, { params }),
  validate: (url: string) => api.post("/rss/validate", null, { params: { url } }),
  stats: () => api.get("/rss/stats/summary"),
};

// === AI ===
export interface AIComposeAssistRequest {
  action: 'generate' | 'improve' | 'platform_versions' | 'hashtags' | 'first_comment' | 'hook_cta' | 'accessibility';
  draft_text?: string;
  prompt?: string;
  platforms?: string[];
  tone?: string;
  locale?: string;
  brand_voice_id?: string;
  media_context?: string;
  target_audience?: string;
  content_type?: string;
}

export interface AIComposeAssistResponse {
  primary_text?: string;
  platform_versions?: Record<string, { text?: string } | string>;
  hashtags?: string[];
  first_comment?: string;
  alt_text?: string;
  hooks?: string[];
  cta_options?: string[];
  quality_score?: number;
  review_notes?: string[];
  differentiators?: string[];
  source_context?: Record<string, unknown>;
}

export interface AICampaignPlanRequest {
  brief: string;
  platforms?: string[];
  days?: number;
  goals?: string[];
  audience?: string;
  content_pillars?: string[];
  brand_voice_id?: string;
  locale?: string;
}

export interface AIPerformanceBriefRequest {
  period_days?: number;
  platforms?: string[];
}

export const aiAPI = {
  generateCaption: (data: {
    topic: string;
    platform?: string;
    tone?: string;
    include_hashtags?: boolean;
    include_emojis?: boolean;
    max_length?: number;
    keywords?: string[];
  }) => api.post("/ai/generate-caption", data),
  generateHashtags: (data: {
    topic: string;
    platform?: string;
    count?: number;
  }) => api.post("/ai/generate-hashtags", data),
  rewrite: (data: {
    original_text: string;
    target_platform: string;
    tone?: string;
  }) => api.post("/ai/rewrite", data),
  adaptForPlatforms: (data: {
    original_text: string;
    target_platforms: string[];
    tone?: string;
  }) => api.post("/ai/adapt-for-platforms", data),
  describeImage: (mediaId: string) =>
    api.post("/ai/describe-image", { media_id: mediaId }),
  contentCalendar: (topic: string, days: number, platforms: string[]) =>
    api.post("/ai/content-calendar", null, { params: { topic, days, platforms } }),
  generateReelScript: (data: {
    topic: string;
    platform?: string;
    duration?: number;
    story_type?: string;
    tone?: string;
    post_id?: string;
  }) => api.post("/ai/generate-reel-script", data),
  generateStoryCards: (data: {
    topic: string;
    platform?: string;
    card_count?: number;
    tone?: string;
    include_stickers?: boolean;
    post_id?: string;
  }) => api.post("/ai/generate-story-cards", data),
  generateCarouselSlides: (data: {
    topic: string;
    platform?: string;
    slide_count?: number;
    tone?: string;
    post_id?: string;
  }) => api.post("/ai/generate-carousel-slides", data),
  composeAssist: (data: AIComposeAssistRequest) =>
    api.post<AIComposeAssistResponse>("/ai/compose-assist", data),
  studioCampaignPlan: (data: AICampaignPlanRequest) =>
    api.post("/ai/studio/campaign-plan", data),
  studioPerformanceBrief: (data: AIPerformanceBriefRequest) =>
    api.post("/ai/studio/performance-brief", data),
};

// === Growth Studio ===
export type GrowthAssetKind =
  | "idea"
  | "hashtag_set"
  | "channel_group"
  | "report_schedule"
  | "reminder_rule"
  | "video_cover_preset"
  | "approval_review_link"
  | "experiment"
  | "agent_action";

export interface GrowthAsset {
  id: string;
  kind: GrowthAssetKind;
  title: string;
  description?: string;
  status: string;
  source: string;
  source_url?: string | null;
  tags: string[];
  hashtags: string[];
  platforms: string[];
  account_ids: string[];
  score: number;
  impact: string;
  effort: string;
  payload: Record<string, unknown>;
  usage_count: number;
  last_used_at?: string | null;
  created_at: string;
  updated_at: string;
}

export interface GrowthAssetsResponse {
  assets: GrowthAsset[];
  total: number;
}

export interface GrowthAssetPayload {
  kind: GrowthAssetKind;
  title: string;
  description?: string;
  status?: string;
  source?: string;
  source_url?: string | null;
  tags?: string[];
  hashtags?: string[];
  platforms?: string[];
  account_ids?: string[];
  score?: number;
  impact?: string;
  effort?: string;
  payload?: Record<string, unknown>;
}

export interface GenerateGrowthIdeasRequest {
  context: string;
  platforms?: string[];
  sources?: string[];
  count?: number;
  save?: boolean;
}

export interface GrowthOpportunityPlanRequest {
  goal: string;
  platforms?: string[];
  period_days?: number;
}

export interface GrowthOSSignal {
  id: string;
  source: string;
  type: string;
  title: string;
  detail: string;
  suggested_action: string;
  confidence: number;
  urgency: string;
  evidence?: Record<string, unknown>;
}

export interface GrowthOSBrandBrain {
  name: string;
  voice: string;
  tone_keywords: string[];
  avoid_words?: string[];
  industry?: string;
  target_audience?: string;
  required_elements?: string[];
  example_posts?: string[];
  high_performing_examples?: Array<Record<string, unknown>>;
  top_hashtags: string[];
  top_patterns: string[];
  confidence: number;
  source_count?: Record<string, number>;
  gaps: string[];
}

export interface GrowthOSCreativeBundle {
  primary_caption: string;
  platform_versions: Record<string, { text?: string } | string>;
  hashtags: string[];
  alt_text?: string;
  media_prompt?: string;
  carousel_brief: string[];
  reel_brief: string[];
  review_notes: string[];
}

export interface GrowthOSStrategy {
  goal: string;
  next_best_post?: {
    title?: string;
    angle?: string;
    why_now?: string;
    target_audience?: string;
    recommended_platforms?: string[];
    confidence?: number;
  };
  weekly_plan: Array<Record<string, unknown>>;
  experiments: Array<Record<string, unknown>>;
  rationale: string[];
}

export interface GrowthOSAction {
  id: string;
  label: string;
  type: "client_apply" | "server_confirm";
  asset_id?: string;
  requires_confirmation: boolean;
  preview: Record<string, unknown>;
}

export interface GrowthOSContextResponse {
  generated_at: string;
  period_days: number;
  platforms: string[];
  brand_brain: GrowthOSBrandBrain;
  signals: GrowthOSSignal[];
  context_summary: string;
  source_counts: Record<string, number>;
  strategy?: GrowthOSStrategy | null;
  creative_bundle?: GrowthOSCreativeBundle | null;
  actions: GrowthOSAction[];
}

export interface GrowthOSStrategyRequest {
  goal: string;
  platforms?: string[];
  period_days?: number;
  save_action?: boolean;
}

export interface GrowthOSStrategyResponse extends GrowthOSContextResponse {
  strategy: GrowthOSStrategy;
  creative_bundle: GrowthOSCreativeBundle;
  actions: GrowthOSAction[];
  agent_asset?: GrowthAsset | null;
  source_context?: Record<string, unknown>;
  ai_used: boolean;
}

export interface GrowthOSConfirmActionResponse {
  confirmed: boolean;
  requires_confirmation?: boolean;
  preview: Record<string, unknown>;
  asset?: GrowthAsset;
  action?: string;
  result?: ConvertGrowthAssetResponse;
}

export interface ConvertGrowthAssetResponse {
  post_id: string;
  compose_url: string;
  asset: GrowthAsset;
}

export const growthAPI = {
  listAssets: (params?: {
    kind?: GrowthAssetKind;
    status?: string;
    search?: string;
    limit?: number;
  }) => api.get<GrowthAssetsResponse>("/growth/assets", { params }),
  createAsset: (data: GrowthAssetPayload) =>
    api.post<GrowthAsset>("/growth/assets", data),
  updateAsset: (id: string, data: Partial<GrowthAssetPayload>) =>
    api.put<GrowthAsset>(`/growth/assets/${id}`, data),
  deleteAsset: (id: string) => api.delete(`/growth/assets/${id}`),
  useAsset: (id: string) => api.post<GrowthAsset>(`/growth/assets/${id}/use`),
  convertToDraft: (id: string) =>
    api.post<ConvertGrowthAssetResponse>(`/growth/assets/${id}/convert-to-draft`),
  generateIdeas: (data: GenerateGrowthIdeasRequest) =>
    api.post<{ ideas: GrowthAsset[]; saved: boolean; ai_used: boolean }>(
      "/growth/ideas/generate",
      data,
    ),
  opportunityPlan: (data: GrowthOpportunityPlanRequest) =>
    api.post<Record<string, unknown>>("/growth/opportunity-plan", data),
  osContext: (params?: { platforms?: string[]; period_days?: number }) =>
    api.get<GrowthOSContextResponse>("/growth/os-context", { params }),
  osStrategy: (data: GrowthOSStrategyRequest) =>
    api.post<GrowthOSStrategyResponse>("/growth/os-strategy", data),
  confirmAction: (
    id: string,
    data: { action: "create_draft"; confirmed?: boolean },
  ) =>
    api.post<GrowthOSConfirmActionResponse>(
      `/growth/assets/${id}/confirm-action`,
      data,
    ),
};

// === Approvals ===
export const approvalsAPI = {
  list: (params?: { status?: string; page?: number }) =>
    api.get("/approvals", { params }),
  stats: () => api.get("/approvals/stats"),
  submit: (postId: string) =>
    api.post("/approvals/submit", { post_id: postId }),
  approve: (postId: string, feedback?: string) =>
    api.post(`/approvals/${postId}/approve`, { feedback }),
  reject: (postId: string, feedback?: string) =>
    api.post(`/approvals/${postId}/reject`, { feedback }),
  requestChanges: (postId: string, feedback: string) =>
    api.post(`/approvals/${postId}/request-changes`, { feedback }),
  resubmit: (postId: string) =>
    api.post(`/approvals/${postId}/resubmit`),
};

// === Billing ===
export const billingAPI = {
  plans: () => api.get("/billing/plans"),
  subscription: () => api.get("/billing/subscription"),
  overview: () => api.get("/billing/overview"),
  checkout: (plan: string, billing_cycle: string = "monthly", idempotency_key?: string) =>
    api.post("/billing/checkout", { plan, billing_cycle, idempotency_key }),
  portal: () => api.post("/billing/portal"),
  devUpgrade: (plan: string, billing_cycle: string = "monthly") =>
    api.post("/billing/dev-upgrade", { plan, billing_cycle }),
};

// === Automation Workflows ===
export const automationsAPI = {
  listWorkflows: (params?: unknown) => api.get("/automations/workflows", { params }),
  getWorkflow: (id: string) => api.get(`/automations/workflows/${id}`),
  createWorkflow: (data: unknown) => api.post("/automations/workflows", data),
  updateWorkflow: (id: string, data: unknown) => api.put(`/automations/workflows/${id}`, data),
  deleteWorkflow: (id: string) => api.delete(`/automations/workflows/${id}`),
  activateWorkflow: (id: string) => api.post(`/automations/workflows/${id}/activate`),
  deactivateWorkflow: (id: string) => api.post(`/automations/workflows/${id}/deactivate`),
  testWorkflow: (id: string, data: unknown) => api.post(`/automations/workflows/${id}/test`, data),
  listExecutions: (id: string, params?: unknown) =>
    api.get(`/automations/workflows/${id}/executions`, { params }),
  getExecution: (id: string) => api.get(`/automations/executions/${id}`),
  listTemplates: (params?: unknown) => api.get("/automations/templates", { params }),
  installTemplate: (id: string) => api.post(`/automations/templates/${id}/install`),
};

// === Unified Inbox ===
export const inboxAPI = {
  listMessages: (params?: unknown) => api.get("/inbox/messages", { params }),
  getMessage: (id: string) => api.get(`/inbox/messages/${id}`),
  replyToMessage: (id: string, data: unknown) => api.post(`/inbox/messages/${id}/reply`, data),
  updateMessage: (id: string, data: unknown) => api.put(`/inbox/messages/${id}`, data),
  bulkAction: (data: unknown) => api.post("/inbox/messages/bulk", data),
  getConversation: (id: string) => api.get(`/inbox/conversations/${id}`),
  listContacts: (params?: unknown) => api.get("/inbox/contacts", { params }),
  getContact: (id: string) => api.get(`/inbox/contacts/${id}`),
  updateContact: (id: string, data: unknown) => api.put(`/inbox/contacts/${id}`, data),
  listCannedResponses: () => api.get("/inbox/canned-responses"),
  createCannedResponse: (data: unknown) => api.post("/inbox/canned-responses", data),
  updateCannedResponse: (id: string, data: unknown) => api.put(`/inbox/canned-responses/${id}`, data),
  deleteCannedResponse: (id: string) => api.delete(`/inbox/canned-responses/${id}`),
  listRules: () => api.get("/inbox/rules"),
  createRule: (data: unknown) => api.post("/inbox/rules", data),
  updateRule: (id: string, data: unknown) => api.put(`/inbox/rules/${id}`, data),
  deleteRule: (id: string) => api.delete(`/inbox/rules/${id}`),
  stats: () => api.get("/inbox/stats"),
};

// === Collaboration Marketplace ===
export const marketplaceAPI = {
  createProfile: (data: unknown) => api.post("/marketplace/profile", data),
  getMyProfile: () => api.get("/marketplace/profile/me"),
  searchCreators: (params?: unknown) => api.get("/marketplace/creators", { params }),
  getCreator: (id: string) => api.get(`/marketplace/creators/${id}`),
  getRecommendations: () => api.get("/marketplace/creators/recommendations"),
  listCampaigns: (params?: unknown) => api.get("/marketplace/campaigns", { params }),
  getCampaign: (id: string) => api.get(`/marketplace/campaigns/${id}`),
  createCampaign: (data: unknown) => api.post("/marketplace/campaigns", data),
  updateCampaign: (id: string, data: unknown) => api.put(`/marketplace/campaigns/${id}`, data),
  listProposals: (campaignId: string) => api.get(`/marketplace/campaigns/${campaignId}/proposals`),
  submitProposal: (campaignId: string, data: unknown) =>
    api.post(`/marketplace/campaigns/${campaignId}/proposals`, data),
  respondToProposal: (id: string, data: unknown) => api.put(`/marketplace/proposals/${id}`, data),
  submitDeliverable: (proposalId: string, data: unknown) =>
    api.post(`/marketplace/proposals/${proposalId}/deliverables`, data),
  reviewDeliverable: (id: string, data: unknown) =>
    api.put(`/marketplace/deliverables/${id}/review`, data),
  leaveReview: (data: unknown) => api.post("/marketplace/reviews", data),
  getReviews: (userId: string) => api.get(`/marketplace/reviews/${userId}`),
  stats: () => api.get("/marketplace/stats"),
};

// === Content Intelligence & AI Repurposing ===
export const contentIntelligenceAPI = {
  // Repurpose
  createRepurposeJob: (data: unknown) => api.post("/content-intelligence/repurpose", data),
  repurpose: (data: unknown) => api.post("/content-intelligence/repurpose", data),
  listRepurposeJobs: (params?: unknown) => api.get("/content-intelligence/repurpose", { params }),
  getRepurposeJobs: (params?: unknown) => api.get("/content-intelligence/repurpose", { params }),
  getRepurposeJob: (id: string) => api.get(`/content-intelligence/repurpose/${id}`),
  clearRecentRepurposeJobs: () => api.delete("/content-intelligence/repurpose/recent"),
  createRepurposeDrafts: (id: string, data: unknown) =>
    api.post(`/content-intelligence/repurpose/${id}/drafts`, data),
  // Brand Voice
  listBrandVoices: () => api.get("/content-intelligence/brand-voice"),
  getBrandVoices: () => api.get("/content-intelligence/brand-voice"),
  getBrandVoice: (id: string) => api.get(`/content-intelligence/brand-voice/${id}`),
  createBrandVoice: (data: unknown) => api.post("/content-intelligence/brand-voice", data),
  updateBrandVoice: (id: string, data: unknown) => api.put(`/content-intelligence/brand-voice/${id}`, data),
  deleteBrandVoice: (id: string) => api.delete(`/content-intelligence/brand-voice/${id}`),
  // Suggestions
  getSuggestions: (params?: unknown) => api.get("/content-intelligence/suggestions", { params }),
  updateSuggestion: (id: string, data: unknown) => api.put(`/content-intelligence/suggestions/${id}`, data),
  convertSuggestion: (id: string) => api.post(`/content-intelligence/suggestions/${id}/convert`),
  // Analytics
  getPerformanceAnalytics: (period?: string) =>
    api.get("/content-intelligence/analytics", { params: period ? { period } : undefined }),
  refreshAnalytics: () => api.post("/content-intelligence/analytics/refresh"),
  // A/B Tests
  listABTests: (params?: unknown) => api.get("/content-intelligence/ab-tests", { params }),
  getABTests: (params?: unknown) => api.get("/content-intelligence/ab-tests", { params }),
  getABTest: (id: string) => api.get(`/content-intelligence/ab-tests/${id}`),
  createABTest: (data: unknown) => api.post("/content-intelligence/ab-tests", data),
  updateABTest: (id: string, data: unknown) => api.put(`/content-intelligence/ab-tests/${id}`, data),
  // Prompt Templates
  listPromptTemplates: (params?: { category?: string; platform?: string; favorites_only?: boolean; search?: string }) =>
    api.get("/content-intelligence/prompts", { params }),
  getPromptTemplate: (id: string) => api.get(`/content-intelligence/prompts/${id}`),
  createPromptTemplate: (data: unknown) => api.post("/content-intelligence/prompts", data),
  updatePromptTemplate: (id: string, data: unknown) => api.put(`/content-intelligence/prompts/${id}`, data),
  deletePromptTemplate: (id: string) => api.delete(`/content-intelligence/prompts/${id}`),
  toggleFavoritePrompt: (id: string) => api.post(`/content-intelligence/prompts/${id}/favorite`),
  usePromptTemplate: (id: string) => api.post(`/content-intelligence/prompts/${id}/use`),

  // Repurpose Intelligence
  generateRepurposeSuggestions: (data?: { min_age_days?: number; max_posts?: number; top_n?: number }) =>
    api.post("/content-intelligence/repurpose-intelligence/generate", data || {}),
  listRepurposeSuggestions: (params?: { skip?: number; limit?: number; status?: string }) =>
    api.get("/content-intelligence/repurpose-intelligence/suggestions", { params }),
  getRepurposeSuggestion: (id: string) =>
    api.get(`/content-intelligence/repurpose-intelligence/suggestions/${id}`),
  getRepurposeGraph: (postId: string) =>
    api.get(`/content-intelligence/repurpose-intelligence/graph/${postId}`),
  updateRepurposeSuggestion: (id: string, data: { status: string; dismissed_reason?: string }) =>
    api.put(`/content-intelligence/repurpose-intelligence/suggestions/${id}`, data),
  convertRepurposeSuggestion: (id: string, data: { new_post_id: string }) =>
    api.post(`/content-intelligence/repurpose-intelligence/suggestions/${id}/convert`, data),
};

// === Live Dynamic Blocks ===
export const liveBlocksAPI = {
  listConfigs: (pageId: string) => api.get("/live-blocks/configs", { params: { page_id: pageId } }),
  getConfig: (id: string) => api.get(`/live-blocks/configs/${id}`),
  createConfig: (data: unknown) => api.post("/live-blocks/configs", data),
  updateConfig: (id: string, data: unknown) => api.put(`/live-blocks/configs/${id}`, data),
  deleteConfig: (id: string) => api.delete(`/live-blocks/configs/${id}`),
  refreshData: (id: string) => api.post(`/live-blocks/configs/${id}/refresh`),
  getData: (id: string) => api.get(`/live-blocks/configs/${id}/data`),
  votePoll: (id: string, data: unknown) => api.post(`/live-blocks/configs/${id}/poll/vote`, data),
  listIntegrations: () => api.get("/live-blocks/integrations"),
  createIntegration: (data: unknown) => api.post("/live-blocks/integrations", data),
  updateIntegration: (id: string, data: unknown) => api.put(`/live-blocks/integrations/${id}`, data),
  deleteIntegration: (id: string) => api.delete(`/live-blocks/integrations/${id}`),
  getPublicBlocks: (pageId: string) => {
    const API_BASE = import.meta.env.VITE_API_URL || "";
    return axios.get(`${API_BASE}/api/live-blocks/public/${pageId}/blocks`);
  },
};

// === Social Listening ===
export const listeningAPI = {
  listKeywords: () => api.get("/listening/keywords"),
  createKeyword: (data: {
    keyword: string;
    keyword_type?: string;
    platforms?: string[];
    language?: string;
  }) => api.post("/listening/keywords", data),
  deleteKeyword: (id: string) => api.delete(`/listening/keywords/${id}`),
  toggleKeyword: (id: string) => api.post(`/listening/keywords/${id}/toggle`),
  getMentions: (params?: {
    keyword_id?: string;
    platform?: string;
    sentiment?: string;
    days?: number;
    limit?: number;
    page?: number;
  }) => api.get("/listening/mentions", { params }),
  getTopMentions: (params?: { days?: number; limit?: number }) =>
    api.get("/listening/mentions/top", { params }),
  getDashboard: (params?: { days?: number; keyword_id?: string }) =>
    api.get("/listening/dashboard", { params }),
  getSentimentTimeline: (params?: { days?: number; keyword_id?: string }) =>
    api.get("/listening/sentiment-timeline", { params }),
  getAlerts: (params?: { unread_only?: boolean; limit?: number }) =>
    api.get("/listening/alerts", { params }),
  markAlertRead: (id: string) => api.put(`/listening/alerts/${id}/read`),
  triggerScan: () => api.post("/listening/scan"),
};

// === AI Image Generation ===
export const aiImagesAPI = {
  generate: (data: {
    prompt: string;
    negative_prompt?: string;
    style?: string;
    aspect_ratio?: string;
    platform?: string;
    save_to_library?: boolean;
    text_in_image?: string;
    seed?: number;
    reference_image_urls?: string[];
    reference_weights?: number[];
    brand_kit_id?: string;
    character_id?: string;
    model_variant?: string;
    variations?: number;
  }) => api.post("/ai/images/generate", data),
  listHistory: (params?: {
    limit?: number;
    page?: number;
    starred_only?: boolean;
    search?: string;
  }) => api.get("/ai/images/history", { params }),
  clearHistory: () => api.delete("/ai/images/history/recent"),
  getGeneration: (id: string) => api.get(`/ai/images/history/${id}`),
  getStyles: () => api.get("/ai/images/styles"),
  getModels: () => api.get("/ai/images/models"),
  getDimensions: () => api.get("/ai/images/dimensions"),
  getPlatformDefaults: () => api.get("/ai/images/platform-defaults"),
  enhancePrompt: (data: {
    prompt: string;
    style?: string;
    platform?: string;
  }) => api.post("/ai/images/enhance-prompt", data),
  estimateCost: (data: {
    aspect_ratio: string;
    model_variant?: string;
    variations?: number;
  }) => api.post("/ai/images/estimate-cost", data),
  moreLikeThis: (jobId: string) =>
    api.post(`/ai/images/history/${jobId}/more-like-this`),
  toggleStar: (jobId: string, starred: boolean) =>
    api.patch(`/ai/images/history/${jobId}/star`, { starred }),
  regenerateAltText: (jobId: string) =>
    api.post(`/ai/images/history/${jobId}/alt-text`),
  upscale: (jobId: string) =>
    api.post(`/ai/images/history/${jobId}/upscale`),
  removeBackground: (jobId: string) =>
    api.post(`/ai/images/history/${jobId}/remove-bg`),
  animate: (jobId: string, durationSeconds = 3) =>
    api.post(`/ai/images/history/${jobId}/animate`, {
      duration_seconds: durationSeconds,
    }),
  inpaint: (jobId: string, maskUrl: string, prompt: string) =>
    api.post(`/ai/images/history/${jobId}/inpaint`, {
      mask_url: maskUrl,
      prompt,
    }),
  // Brand kits
  listBrandKits: () => api.get("/ai/images/brand-kits"),
  createBrandKit: (data: BrandKitPayload) =>
    api.post("/ai/images/brand-kits", data),
  updateBrandKit: (id: string, data: BrandKitPayload) =>
    api.patch(`/ai/images/brand-kits/${id}`, data),
  deleteBrandKit: (id: string) => api.delete(`/ai/images/brand-kits/${id}`),
  // Characters
  listCharacters: () => api.get("/ai/images/characters"),
  createCharacter: (data: CharacterPayload) =>
    api.post("/ai/images/characters", data),
  deleteCharacter: (id: string) => api.delete(`/ai/images/characters/${id}`),
  rotateImage: (jobId: string, angle: number) =>
    api.post("/ai/images/rotate", { job_id: jobId, angle }),
};

export interface BrandKitPayload {
  name: string;
  logo_url?: string;
  primary_color?: string;
  secondary_color?: string;
  accent_colors?: string[];
  primary_font?: string;
  secondary_font?: string;
  palette_description?: string;
  mood_keywords?: string[];
  banned_elements?: string[];
  is_default?: boolean;
}

export interface CharacterPayload {
  name: string;
  description: string;
  reference_image_urls?: string[];
  style_token?: string;
}

// === AI Usage & Pricing ===
export const aiUsageAPI = {
  getSummary: (params?: { days?: number }) =>
    api.get("/ai/usage/summary", { params }),
  /** P3-4: server streams a CSV of AISpendRecord rows for the given
   *  window. Returns the absolute URL (with `Authorization` cookie
   *  or token) — callers should navigate the browser to it rather
   *  than fetching, so the browser handles the file download. */
  exportUrl: (days: number) =>
    `${import.meta.env.VITE_API_URL || ''}/api/ai-usage/export.csv?days=${encodeURIComponent(String(days))}`,
  /** P3-2: per-tool latency/error-rate aggregates. */
  getTelemetry: (params?: { days?: number }) =>
    api.get('/ai/usage/telemetry', { params }),
};

// === AI Orchestrator ===
export const orchestratorAPI = {
  generate: (data: {
    topic: string;
    target_platforms: string[];
    target_account_ids?: string[];
    tone?: string;
    brand_voice?: string;
    media_ids?: string[];
    instructions?: string;
    pipeline?: string;
    stop_before?: string;
    publish_mode?: string;
    custom_schedule_times?: Record<string, string>;
    review_mode?: string;
    max_retries?: number;
  }) => api.post("/orchestrator/generate", data),

  optimize: (data: {
    text: string;
    target_platforms: string[];
    target_account_ids?: string[];
    tone?: string;
    brand_voice?: string;
    media_ids?: string[];
    publish_mode?: string;
    custom_schedule_times?: Record<string, string>;
  }) => api.post("/orchestrator/optimize", data),

  resume: (pipelineId: string, stop_before?: string) =>
    api.post(`/orchestrator/${pipelineId}/resume`, { stop_before }),

  get: (pipelineId: string) =>
    api.get(`/orchestrator/${pipelineId}`),

  createPosts: (pipelineId: string, publishMode?: string, scheduleTimes?: Record<string, string>) =>
    api.post(`/orchestrator/${pipelineId}/create-posts`, {
      publish_mode: publishMode || "draft",
      custom_schedule_times: scheduleTimes,
    }),

  history: (params?: { status?: string; limit?: number; skip?: number }) =>
    api.get("/orchestrator/history", { params }),
  getRunDetail: (runId: string) => api.get(`/orchestrator/history/${runId}`),

  calendar: (data: {
    topic: string;
    days?: number;
    target_platforms: string[];
    target_account_ids?: string[];
    tone?: string;
    brand_voice?: string;
    instructions?: string;
    posts_per_day?: number;
    create_drafts?: boolean;
  }) => api.post("/orchestrator/calendar", data),

  strategist: (data: {
    days?: number;
    target_platforms: string[];
    target_account_ids?: string[];
    tone?: string;
    brand_voice?: string;
    instructions?: string;
    goals?: string;
    posts_per_day?: number;
    review_mode?: string;
    max_retries?: number;
  }) => api.post("/orchestrator/strategist", data),

  patchContent: (pipelineId: string, platformContent: Record<string, { text: string; hashtags: string[] }>) =>
    api.patch(`/orchestrator/${pipelineId}/content`, { platform_content: platformContent }),

  pipelinesInfo: () => api.get("/orchestrator/pipelines-info"),

  // Prompt templates
  getPromptDefaults: () => api.get("/orchestrator/prompts/defaults"),
  getPromptOverrides: () => api.get("/orchestrator/prompts/overrides"),
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  savePromptOverrides: (data: Record<string, any>) => api.put("/orchestrator/prompts/overrides", data),
  resetPromptOverrides: () => api.delete("/orchestrator/prompts/overrides"),
};

// ---------------------------------------------------------------------------
// Agent Conversations API
// ---------------------------------------------------------------------------

export interface AgentConversationSummary {
  id: string;
  title: string;
  is_active: boolean;
  message_count: number;
  total_input_tokens: number;
  total_output_tokens: number;
  total_cost: number;
  created_at: string;
  updated_at: string;
}

export interface AgentConversationMessage {
  role: string;
  content: string;
  timestamp: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  tool_calls?: any[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  followups?: any[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  inline_blocks?: any[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  token_usage?: any;
}

export interface AgentConversationDetail {
  id: string;
  user_id: string;
  title: string;
  messages: AgentConversationMessage[];
  is_active: boolean;
  total_input_tokens: number;
  total_output_tokens: number;
  total_cost: number;
  created_at: string;
  updated_at: string;
}

export const agentConversationsAPI = {
  /** List user's conversations, sorted by most recent. */
  list: (params?: { skip?: number; limit?: number }) =>
    api.get<AgentConversationSummary[]>("/agent/conversations", { params }),

  /** Get full conversation detail with all messages. */
  get: (conversationId: string) =>
    api.get<AgentConversationDetail>(`/agent/conversations/${conversationId}`),

  /** Rename a conversation. */
  rename: (conversationId: string, title: string) =>
    api.patch(`/agent/conversations/${conversationId}/title`, { title }),

  /** Delete a conversation. */
  delete: (conversationId: string) =>
    api.delete(`/agent/conversations/${conversationId}`),

  /** Delete ALL conversations for the current user. */
  clearAll: () => api.delete<{ deleted: number }>("/agent/conversations/all"),
};

// ─────────────────────────────────────────────────────────────────
// Agent provider/model picker (#26)
// ─────────────────────────────────────────────────────────────────

export interface AgentProviderModel {
  id: string;
  label: string;
  tier?: string;
}

export interface AgentProviderInfo {
  name: string;
  label: string;
  models: AgentProviderModel[];
}

export interface AgentProvidersResponse {
  providers: AgentProviderInfo[];
}

export const agentProvidersAPI = {
  /** List installed AI providers and their available models. */
  list: () => api.get<AgentProvidersResponse>("/agent/providers"),
};

export default api;
